import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BarChart3,
  Bell,
  Bike,
  Building2,
  ChevronRight,
  ChevronsLeft,
  CircleDollarSign,
  ClipboardList,
  FileSpreadsheet,
  FileText,
  Handshake,
  History,
  LayoutDashboard,
  ListChecks,
  LogOut,
  MapPinned,
  Menu,
  MessageSquare,
  Package,
  PackageSearch,
  PackagePlus,
  PanelLeftOpen,
  Plug,
  PlugZap,
  Search,
  Settings,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Store,
  UserSearch,
  Truck,
  UserCog,
  UserPlus,
  Users,
  Wallet,
  X,
} from "lucide-react";

import { AuthContext } from "../../context/AuthContext";
import LanguageSwitcher from "../../misc/LanguageSwitcher";
import ThemeToggle from "../../misc/ThemeToggle";

/* =======================
   PERMISSION HELPER
   ======================= */
export function canSeeItem(userPermissions, itemPermission) {
  if (!itemPermission) return true; // no restriction → visible to all
  if (!userPermissions) return true; // still loading → show all
  if (userPermissions.includes("*")) return true; // full access (parent)
  if (itemPermission === "*") return false; // full-access-only item
  return userPermissions.includes(itemPermission); // child: check specific perm
}

/* =======================
   CHARTE TEQA : une seule couleur de marque (variables --teqa-* de styles/tokens.css),
   identique à teqa_confirmation et teqa_connect.
   ======================= */
const BRAND = {
  solid: "bg-[var(--teqa-green)] shadow-[0_6px_16px_-6px_var(--teqa-green)]",
  soft: "bg-[var(--teqa-green-dim)] text-[var(--teqa-green)]",
  active: "border-l-[3px] border-[var(--teqa-green)] bg-[var(--teqa-green-dim)]",
  text: "text-[var(--teqa-green)]",
};

/* =======================
   DATA (EDIT THIS ONLY)
   Chemins et permissions = routes d'AppCommercant / codes du backend auth.
   ======================= */
export const SIDEBAR_NAV = [
  {
    title: "Tableau de bord",
    short: "Accueil",
    icon: LayoutDashboard,
    description: "Vue d'ensemble de votre activité",
    items: [
      { label: "Vue d'ensemble", hint: "Chiffres clés du store", path: "/dashboard", icon: LayoutDashboard, disabled: true, permission: "dashboard.view" },
      { label: "Analyses", hint: "Tendances et performances", path: "/dashboard/analytics", icon: BarChart3, disabled: true, permission: "dashboard.view" },
    ],
  },
  {
    title: "Commandes",
    short: "Commandes",
    icon: ShoppingCart,
    description: "De la réception à la livraison",
    items: [
      { label: "Tableau des commandes", hint: "Indicateurs et activité", path: "/management/orders/dashboard", icon: LayoutDashboard, permission: "orders.view" },
      { label: "Nouvelle commande", hint: "Saisie manuelle", path: "/management/orders/add", icon: PackagePlus, permission: "orders.assign" },
      { label: "En attente", hint: "Appeler et confirmer", path: "/management/orders/pending", icon: ListChecks, permission: "orders.view" },
      { label: "Expédition", hint: "Envoyer chez le transporteur", path: "/management/orders/delivered", icon: Truck, permission: "orders.view" },
      { label: "Toutes les commandes", hint: "Historique complet", path: "/management/orders", icon: ClipboardList, permission: "orders.view" },
      { label: "Suivi des colis", hint: "Statuts transporteurs", path: "/management/tracking", icon: MapPinned, permission: "orders.view" },
      { label: "Importer un fichier", hint: "Excel ou CSV", path: "/management/orders/add_data", icon: FileSpreadsheet, permission: "orders.assign" },
    ],
  },
  {
    title: "Agents de confirmation",
    short: "Confirmation",
    icon: Handshake,
    description: "Confier la confirmation de vos commandes à un agent ou une agence",
    items: [
      { label: "Trouver un agent / une agence", hint: "Comparer les profils et demander une collaboration", path: "/admin-marketplace/profiles", icon: UserSearch, permission: "marketplace.view" },
      { label: "Mes collaborations", hint: "Demandes, négociations, contrats actifs", path: "/admin-marketplace/collaborations", icon: Handshake, permission: "collaborations.view" },
      { label: "Tableau marketplace", hint: "Activité des partenaires", path: "/admin-marketplace/dashboard", icon: BarChart3, permission: "marketplace.view" },
      { label: "Performance", hint: "Confirmation et livraison par collaboration", path: "/admin-marketplace/performance", icon: BarChart3, permission: "collaborations.view" },
      { label: "Portefeuille", hint: "Solde et recharges", path: "/admin-marketplace/wallet", icon: Wallet, permission: "wallets.view_own" },
      { label: "Commissions", hint: "Par commande livrée", path: "/admin-marketplace/commissions", icon: CircleDollarSign, permission: "commissions.view_own" },
      { label: "Factures", hint: "Paiements aux agences", path: "/admin-marketplace/invoices", icon: FileText, permission: "invoices.view_own" },
      { label: "Messages", hint: "Discuter avec les agences", path: "/admin-marketplace/messaging", icon: MessageSquare, permission: "messaging.view_own" },
      { label: "Notifications", hint: "Alertes marketplace", path: "/admin-marketplace/notifications", icon: Bell, permission: "messaging.view_own" },
      { label: "Journal d'audit", hint: "Qui a fait quoi", path: "/admin-marketplace/audit-logs", icon: History, permission: "*" },
    ],
  },
  {
    title: "Clients",
    short: "Clients",
    icon: Users,
    description: "Votre base clients et leur fiabilité",
    items: [
      { label: "Tableau clients", hint: "Fidélité et retours", path: "/clients/dashboard", icon: BarChart3, permission: "orders.view" },
      { label: "Liste des clients", hint: "Rechercher, modifier", path: "/clients/list", icon: Users, permission: "orders.view" },
      { label: "Ajouter un client", hint: "Nouvelle fiche", path: "/clients/Add", icon: UserPlus, permission: "orders.assign" },
    ],
  },
  {
    title: "Boutiques",
    short: "Boutiques",
    icon: Store,
    description: "YouCan, Shopify et vos produits",
    items: [
      { label: "Tableau des intégrations", hint: "État des synchronisations", path: "/integration", icon: PlugZap, permission: "integrations.view" },
      { label: "Connecter une boutique", hint: "Nouvelle plateforme", path: "/integrations/add", icon: Plug, permission: "integrations.manage" },
      { label: "Boutiques connectées", hint: "Gérer les connexions", path: "/integrations/list", icon: Store, permission: "integrations.view" },
      { label: "Produits", hint: "Catalogue importé", path: "/integrations/products", icon: Package, permission: "integrations.view" },
    ],
  },
  {
    title: "Livraison",
    short: "Livraison",
    icon: Truck,
    description: "Vos transporteurs et leurs comptes",
    items: [
      { label: "Connecter un transporteur", hint: "Ozon, Ameex, Sendit…", path: "/delivery/add-company", icon: Truck, permission: "*" },
      { label: "Mes transporteurs", hint: "Comptes et transporteur par défaut", path: "/delivery/companies", icon: Building2, permission: "*" },
      { label: "Expéditions", hint: "Colis et historique de suivi", path: "/delivery/shipments", icon: PackageSearch, permission: "*" },
    ],
  },
  {
    title: "Abonnement",
    short: "Abonnement",
    icon: ShieldCheck,
    description: "Votre formule teqaConnect",
    items: [
      { label: "Mon abonnement", hint: "Formule et limites", path: "/subscriptions/dashboard", icon: ShieldCheck, permission: "*" },
      { label: "Offres et factures", hint: "Offres, paiements passés", path: "/subscriptions/list", icon: FileText, permission: "*" },
    ],
  },
  {
    title: "Marketplace livreurs",
    short: "Livreurs",
    icon: Bike,
    description: "Livreurs indépendants",
    items: [
      { label: "Tableau livreurs", hint: "Bientôt disponible", path: "/marketplaceLivreur/dashboard", icon: Bike, disabled: true, permission: "*" },
      { label: "Offres livreurs", hint: "Bientôt disponible", path: "/marketplaceLivreur/offers", icon: ShoppingBag, disabled: true, permission: "*" },
      { label: "Offres terminées", hint: "Bientôt disponible", path: "/marketplaceLivreur/completed", icon: ListChecks, disabled: true, permission: "*" },
    ],
  },
  {
    title: "Équipe",
    short: "Équipe",
    icon: UserCog,
    description: "Vos collaborateurs et leurs droits",
    items: [
      { label: "Utilisateurs", hint: "Droits et accès", path: "/users/list", icon: Users, permission: "team.view" },
      { label: "Ajouter un utilisateur", hint: "Inviter un membre", path: "/users/add", icon: UserPlus, permission: "team.create" },
    ],
  },
  {
    title: "Compte",
    short: "Compte",
    icon: Settings,
    description: "Profil, store et préférences",
    items: [{ label: "Paramètres", hint: "Profil et informations du store", path: "/settings", icon: Settings }],
  },
];

export function flattenRoutes(groups) {
  return groups.flatMap((g) => g.items.map((it) => ({ label: it.label, path: it.path })));
}

// Page active = chemin le plus long qui préfixe l'URL
function findActivePath(groups, pathname) {
  let best = "";
  groups.forEach((g) =>
    g.items.forEach(({ path }) => {
      const match = pathname === path || pathname.startsWith(`${path}/`);
      if (match && path.length > best.length) best = path;
    })
  );
  return best;
}

const normalize = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const userDisplay = (user) => {
  const name =
    user?.store?.name ||
    user?.store_name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.username ||
    user?.email ||
    "Mon store";
  const initials =
    String(name)
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0] || "")
      .join("")
      .toUpperCase() || "T";
  return { name, initials, sub: user?.email && user.email !== name ? user.email : "Compte store" };
};

const RAIL = 72; // largeur de la barre d'icônes (px) — AppCommercant : sm:pl-[72px]
const PANEL = 248; // largeur du panneau des pages — AppCommercant : sm:pl-[320px] (72 + 248)

/* =======================
   SIDEBAR
   Barre d'icônes (un menu = une icône) + panneau des pages du menu choisi.
   - menu ouvert  : barre + panneau épinglé
   - menu réduit  : barre seule ; un clic sur une icône ouvre le panneau en surimpression
   - mobile       : barre du haut + tiroir (barre + panneau)
   ======================= */
export function Sidebar({ groups = SIDEBAR_NAV, collapsed, setCollapsed, userPermissions = null }) {
  const { logout, user } = useContext(AuthContext);
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [floatingOpen, setFloatingOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [tooltip, setTooltip] = useState(null);
  const [radial, setRadial] = useState(null); // { title, top } : menu circulaire (menu réduit)
  const asideRef = useRef(null);
  const searchRef = useRef(null);

  const filteredGroups = useMemo(
    () =>
      groups
        .map((group) => ({ ...group, items: group.items.filter((item) => canSeeItem(userPermissions, item.permission)) }))
        .filter((group) => group.items.length > 0),
    [groups, userPermissions]
  );

  const activePath = useMemo(() => findActivePath(filteredGroups, pathname), [filteredGroups, pathname]);
  const activeGroup = filteredGroups.find((g) => g.items.some((it) => it.path === activePath));
  const activeItem = activeGroup?.items.find((it) => it.path === activePath);

  const [selectedTitle, setSelectedTitle] = useState(() => activeGroup?.title || filteredGroups[0]?.title);
  const selected = filteredGroups.find((g) => g.title === selectedTitle) || activeGroup || filteredGroups[0];

  // Changement de page : le panneau suit le menu de la page, surimpression/tiroir fermés
  useEffect(() => {
    if (activeGroup?.title) setSelectedTitle(activeGroup.title);
    setFloatingOpen(false);
    setMobileOpen(false);
    setRadial(null);
    setQuery("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Échap ferme ; clic en dehors ferme la surimpression ; tiroir ouvert = page figée
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setFloatingOpen(false);
      setMobileOpen(false);
      setRadial(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    if (!floatingOpen) return undefined;
    const onDown = (e) => !asideRef.current?.contains(e.target) && setFloatingOpen(false);
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [floatingOpen]);
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  // Ctrl/⌘ + K : recherche de page
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (collapsed && !mobileOpen) setFloatingOpen(true);
        setTimeout(() => searchRef.current?.focus(), 30);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [collapsed, mobileOpen]);

  const pinned = !collapsed || mobileOpen;
  const panelVisible = pinned || floatingOpen;
  const me = userDisplay(user);

  const onGroupClick = (title, event) => {
    setQuery("");
    setTooltip(null);
    setSelectedTitle(title);
    if (pinned) return;
    // menu réduit : les pages du menu s'ouvrent en cercle autour de son icône ;
    // 2e clic sur le même menu = fermer
    setFloatingOpen(false);
    const rect = event.currentTarget.getBoundingClientRect();
    setRadial((current) => (current?.title === title ? null : { title, top: rect.top + rect.height / 2 }));
  };

  const showTooltip = (event, label) => {
    if (panelVisible || radial) return; // les noms sont déjà visibles
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({ label, top: rect.top + rect.height / 2 });
  };
  const hideTooltip = () => setTooltip(null);

  // Résultats de recherche (toutes les pages)
  const results = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return null;
    return filteredGroups.flatMap((g) =>
      g.items
        .filter((it) => normalize(`${it.label} ${it.hint || ""} ${g.title}`).includes(q))
        .map((it) => ({ ...it, group: g }))
    );
  }, [filteredGroups, query]);

  return (
    <>
      <style>
        {"@keyframes teqa-panel{from{opacity:0;transform:translateX(-10px)}to{opacity:1;transform:none}}" +
          "@keyframes teqa-tile{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}" +
          "@keyframes teqa-orbit{to{transform:translate(-50%,-50%) rotate(360deg)}}" +
          "@keyframes teqa-pop{from{opacity:0;transform:translate(-50%,-50%) scale(.6)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}"}
      </style>

      {/* Barre supérieure mobile */}
      <header className="fixed inset-x-0 top-0 z-40 flex h-14 items-center gap-3 border-b border-[var(--teqa-border)] bg-[var(--teqa-sidebar)] px-4 sm:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--teqa-text)] hover:bg-[var(--teqa-surface2)]"
          aria-label="Ouvrir le menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <BrandMark />
        <span className="min-w-0 truncate text-sm font-semibold text-[var(--teqa-text)]">{activeItem?.label || "TEQA"}</span>
      </header>

      {/* Voile mobile */}
      <div
        aria-hidden="true"
        onClick={() => setMobileOpen(false)}
        className={`fixed inset-0 z-40 bg-slate-950/50 backdrop-blur-[2px] transition-opacity duration-300 sm:hidden ${
          mobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />

      <aside
        ref={asideRef}
        className={`fixed inset-y-0 left-0 z-50 flex transition-transform duration-300 ease-out ${
          mobileOpen ? "translate-x-0" : "-translate-x-full sm:translate-x-0"
        }`}
        aria-label="Navigation principale"
      >
        {/* ---------------- Barre d'icônes ---------------- */}
        <div className="relative z-10 flex h-full flex-col items-center border-r border-[var(--teqa-border)] bg-[var(--teqa-sidebar)] py-3" style={{ width: RAIL }}>
          <BrandMark />

          {/* Menu réduit : bouton bien visible pour rouvrir le panneau des pages */}
          {collapsed && !mobileOpen ? (
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              aria-label="Ouvrir le menu"
              title="Ouvrir le menu"
              className="mt-3 hidden w-14 flex-col items-center gap-0.5 rounded-xl border border-[var(--teqa-border-md)] bg-[var(--teqa-green-dim)] py-1.5 text-[var(--teqa-green)] transition hover:bg-[var(--teqa-green)] hover:text-white sm:flex"
            >
              <PanelLeftOpen className="h-[18px] w-[18px]" />
              <span className="text-[10px] font-semibold leading-none">Ouvrir</span>
            </button>
          ) : null}

          <nav className="mt-4 flex w-full flex-1 flex-col items-center gap-1 overflow-y-auto px-2 [scrollbar-width:none]">
            {filteredGroups.map((group) => {
              const accent = BRAND;
              const Icon = group.icon;
              const isSelected = (panelVisible && selected?.title === group.title) || radial?.title === group.title;
              const isActive = activeGroup?.title === group.title;
              const soon = group.items.every((it) => it.disabled);
              return (
                <button
                  key={group.title}
                  type="button"
                  onClick={(e) => onGroupClick(group.title, e)}
                  onMouseEnter={(e) => showTooltip(e, group.title)}
                  onMouseLeave={hideTooltip}
                  aria-pressed={isSelected}
                  aria-label={group.title}
                  className="group relative flex w-full flex-col items-center gap-1 rounded-xl py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--teqa-green)]"
                >
                  {isSelected ? <span className="absolute -left-2 top-[26px] h-7 w-1 -translate-y-1/2 rounded-r-full bg-[var(--teqa-green)]" /> : null}
                  <span
                    className={`relative flex h-10 w-10 items-center justify-center rounded-xl transition-all duration-200 ${
                      isSelected
                        ? `${accent.solid} text-white shadow-lg`
                        : isActive
                        ? accent.soft
                        : soon
                        ? "text-[var(--teqa-hint)] opacity-60 group-hover:bg-[var(--teqa-surface2)]"
                        : "text-[var(--teqa-muted)] group-hover:bg-[var(--teqa-surface2)] group-hover:text-[var(--teqa-text)]"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    {isActive && !isSelected ? (
                      <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-[var(--teqa-sidebar)] bg-[var(--teqa-success)]" />
                    ) : null}
                  </span>
                  <span
                    className={`w-full truncate px-0.5 text-center text-[10px] font-medium leading-tight transition ${
                      isSelected || isActive ? "font-semibold text-[var(--teqa-green)]" : "text-[var(--teqa-muted)] group-hover:text-[var(--teqa-text)]"
                    }`}
                  >
                    {group.short || group.title}
                  </span>
                </button>
              );
            })}
          </nav>

          {/* Actions bas de barre */}
          <div className="mt-2 flex flex-col items-center gap-1.5 border-t border-[var(--teqa-border)] pt-3">
            <Link
              to="/settings"
              onMouseEnter={(e) => showTooltip(e, me.name)}
              onMouseLeave={hideTooltip}
              aria-label="Mon compte"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--teqa-green)] text-xs font-bold text-white ring-2 ring-[var(--teqa-green-dim)] transition hover:ring-[var(--teqa-green)]"
            >
              {me.initials}
            </Link>
            <RailAction label="Se déconnecter" onClick={logout} onHover={showTooltip} onLeave={hideTooltip} danger>
              <LogOut className="h-[18px] w-[18px]" />
            </RailAction>
          </div>
        </div>

        {/* ---------------- Panneau des pages ---------------- */}
        {panelVisible && selected ? (
          <div
            className={`flex h-full flex-col border-r border-[var(--teqa-border)] bg-[var(--teqa-sidebar)] ${
              pinned ? "" : "absolute inset-y-0 shadow-2xl shadow-slate-900/20 animate-[teqa-panel_.18s_ease-out]"
            }`}
            style={{ width: PANEL, left: pinned ? undefined : RAIL }}
          >
            <PanelHeader
              group={selected}
              pinned={pinned}
              mobile={mobileOpen}
              onCollapse={() => setCollapsed(true)}
              onPin={() => {
                setCollapsed(false);
                setFloatingOpen(false);
              }}
              onClose={() => (mobileOpen ? setMobileOpen(false) : setFloatingOpen(false))}
            />

            {/* Recherche de page */}
            <div className="px-3 pb-2">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--teqa-hint)]" />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Aller à une page…"
                  className="w-full rounded-lg border border-[var(--teqa-border-md)] bg-[var(--teqa-bg)] py-1.5 pl-8 pr-12 text-xs text-[var(--teqa-text)] outline-none transition placeholder:text-[var(--teqa-hint)] focus:border-[var(--teqa-green)] focus:ring-2 focus:ring-[var(--teqa-green-dim)]"
                />
                <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border border-[var(--teqa-border-md)] bg-[var(--teqa-surface)] px-1 text-[10px] text-[var(--teqa-hint)] sm:block">
                  Ctrl K
                </kbd>
              </label>
            </div>

            <div className="flex-1 overflow-y-auto px-3 pb-3 [scrollbar-width:thin]">
              {results ? (
                results.length ? (
                  <ul className="space-y-1">
                    {results.map((it) => (
                      <li key={it.path}>
                        <PageTile item={it} accent={BRAND} active={it.path === activePath} groupLabel={it.group.title} />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="px-2 py-6 text-center text-xs text-[var(--teqa-hint)]">Aucune page ne correspond.</p>
                )
              ) : (
                <ul key={selected.title} className="space-y-1">
                  {selected.items.map((it, index) => (
                    <li key={it.path} className="animate-[teqa-tile_.22s_ease-out_both]" style={{ animationDelay: `${index * 25}ms` }}>
                      <PageTile item={it} accent={BRAND} active={it.path === activePath} />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Pied du panneau */}
            <div className="flex items-center justify-between gap-2 border-t border-[var(--teqa-border)] px-3 py-2.5">
              <span className="min-w-0">
                <span className="block truncate text-xs font-semibold text-[var(--teqa-text)]">{me.name}</span>
                <span className="block truncate text-[11px] text-[var(--teqa-hint)]">{me.sub}</span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <LanguageSwitcher />
                <ThemeToggle />
              </span>
            </div>
          </div>
        ) : null}
      </aside>

      {/* Menu circulaire (menu réduit) */}
      {radial && !pinned ? (
        <RadialMenu
          group={filteredGroups.find((g) => g.title === radial.title)}
          anchorTop={radial.top}
          activePath={activePath}
          onClose={() => setRadial(null)}
        />
      ) : null}

      {/* Infobulle de la barre d'icônes (menu réduit) */}
      {tooltip ? (
        <div
          role="tooltip"
          className="pointer-events-none fixed z-[60] -translate-y-1/2 whitespace-nowrap rounded-lg border border-[var(--teqa-border-md)] bg-[var(--teqa-surface2)] px-2.5 py-1.5 text-xs font-medium text-[var(--teqa-text)] shadow-lg"
          style={{ top: tooltip.top, left: RAIL + 8 }}
        >
          {tooltip.label}
        </div>
      ) : null}
    </>
  );
}

/* =======================
   SUB COMPONENTS
   ======================= */

/**
 * Menu circulaire : l'icône du menu au centre, ses pages en icônes sur un
 * cercle autour. Les icônes sortent du centre en cascade à l'ouverture.
 */
function RadialMenu({ group, anchorTop, activePath, onClose }) {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setExpanded(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  if (!group) return null;
  const accent = BRAND;
  const GroupIcon = group.icon;
  const count = group.items.length;
  // rayon selon le nombre de pages (assez d'espace pour les libellés)
  const radius = count <= 3 ? 92 : Math.min(150, 70 + count * 9);
  const margin = 56; // place pour les libellés sous les icônes
  const cx = RAIL + radius + margin;
  const viewport = typeof window !== "undefined" ? window.innerHeight : 800;
  const cy = Math.min(Math.max(anchorTop, radius + margin), viewport - radius - margin);

  return (
    // la barre d'icônes reste cliquable : le voile commence après elle
    <div className="fixed inset-y-0 right-0 z-[55]" style={{ left: RAIL }} role="dialog" aria-modal="true" aria-label={group.title}>
      {/* voile : clic = fermer */}
      <button
        type="button"
        aria-label="Fermer le menu"
        onClick={onClose}
        className={`absolute inset-0 cursor-default bg-slate-950/25 backdrop-blur-[3px] transition-opacity duration-300 ${expanded ? "opacity-100" : "opacity-0"}`}
      />

      <div className="absolute" style={{ left: cx - RAIL, top: cy }}>
        {/* anneau décoratif */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-0 top-0 rounded-full border border-dashed border-[var(--teqa-border-md)]"
          style={{
            width: radius * 2,
            height: radius * 2,
            transform: "translate(-50%,-50%)",
            animation: "teqa-orbit 40s linear infinite",
            opacity: expanded ? 1 : 0,
            transition: "opacity .3s",
          }}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-0 top-0 rounded-full bg-[var(--teqa-sidebar)] shadow-2xl shadow-black/20 ring-1 ring-[var(--teqa-border-md)]"
          style={{
            width: radius * 2 + 96,
            height: radius * 2 + 96,
            transform: `translate(-50%,-50%) scale(${expanded ? 1 : 0.4})`,
            opacity: expanded ? 1 : 0,
            transition: "transform .35s cubic-bezier(.2,.9,.3,1.2), opacity .25s",
          }}
        />

        {/* titre du menu */}
        <span
          className="pointer-events-none absolute left-0 whitespace-nowrap rounded-full bg-[var(--teqa-green)] px-3 py-1 text-xs font-semibold text-white shadow-lg"
          style={{
            top: -(radius + 44),
            transform: `translate(-50%, ${expanded ? 0 : 8}px)`,
            opacity: expanded ? 1 : 0,
            transition: "transform .3s ease-out .1s, opacity .3s ease-out .1s",
          }}
        >
          {group.title}
        </span>

        {/* centre : icône du menu (clic = fermer) */}
        <button
          type="button"
          onClick={onClose}
          aria-label={`Fermer ${group.title}`}
          className={`absolute left-0 top-0 flex h-16 w-16 items-center justify-center rounded-full text-white shadow-xl ring-4 ring-[var(--teqa-sidebar)] transition-transform hover:scale-105 ${accent.solid}`}
          style={{ animation: "teqa-pop .25s ease-out both" }}
        >
          <GroupIcon className="h-7 w-7" />
        </button>

        {/* pages du menu sur le cercle */}
        {group.items.map((item, index) => {
          const angle = (-90 + (index * 360) / count) * (Math.PI / 180);
          const x = Math.round(radius * Math.cos(angle));
          const y = Math.round(radius * Math.sin(angle));
          const Icon = item.icon;
          const active = item.path === activePath;
          const style = {
            transform: expanded
              ? `translate(calc(${x}px - 50%), ${y - 24}px) scale(1)`
              : "translate(-50%, -24px) scale(.3)",
            opacity: expanded ? 1 : 0,
            transition: `transform .4s cubic-bezier(.2,.9,.3,1.25) ${index * 35}ms, opacity .25s ease-out ${index * 35}ms`,
          };
          const bubble = (
            <>
              <span
                className={`flex h-12 w-12 items-center justify-center rounded-full shadow-lg ring-1 transition-transform duration-200 group-hover:scale-110 ${
                  item.disabled
                    ? "bg-[var(--teqa-surface2)] text-[var(--teqa-hint)] ring-[var(--teqa-border)]"
                    : active
                    ? `${accent.solid} text-white ring-white`
                    : `ring-[var(--teqa-border-md)] ${accent.soft}`
                }`}
              >
                <Icon className="h-5 w-5" />
              </span>
              <span
                className={`mt-1.5 max-w-[96px] truncate rounded-md px-1.5 py-0.5 text-center text-[11px] font-semibold leading-tight shadow-sm ${
                  active ? "bg-[var(--teqa-green)] text-white" : "bg-[var(--teqa-surface)] text-[var(--teqa-text)]"
                }`}
              >
                {item.label}
              </span>
              {item.disabled ? (
                <span className="absolute -right-1 top-0 rounded-full bg-[var(--teqa-warning)] px-1 text-[9px] font-bold text-white">Bientôt</span>
              ) : null}
            </>
          );
          const className = "group absolute left-0 top-0 flex w-24 flex-col items-center focus:outline-none";
          return item.disabled ? (
            <span key={item.path} className={`${className} cursor-not-allowed`} style={style} title={`${item.label} — bientôt disponible`} aria-disabled="true">
              {bubble}
            </span>
          ) : (
            <Link
              key={item.path}
              to={item.path}
              className={`${className} [&:focus-visible>span:first-child]:ring-4 [&:focus-visible>span:first-child]:ring-[var(--teqa-green)]`}
              style={style}
              title={item.hint ? `${item.label} — ${item.hint}` : item.label}
              aria-current={active ? "page" : undefined}
            >
              {bubble}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
// Logo TEQA : même pyramide que teqa_confirmation (components/ui/TeqaLogo.jsx)
function BrandMark() {
  return (
    <Link
      to="/management/orders/dashboard"
      aria-label="TEQA — accueil"
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--teqa-green-dim)] transition hover:scale-105"
    >
      <svg width="18" height="22" viewBox="0 0 18 22" fill="none" aria-hidden="true">
        <polygon points="9,0 18,8 0,8" fill="#22c55e" />
        <rect x="2" y="10" width="14" height="4" rx="1" fill="#3b82f6" />
        <rect x="0" y="16" width="18" height="4" rx="1" fill="#ef4444" />
      </svg>
    </Link>
  );
}

function PanelHeader({ group, pinned, mobile, onCollapse, onPin, onClose }) {
  const accent = BRAND;
  const Icon = group.icon;
  return (
    <div className="flex items-start gap-3 px-4 pb-3 pt-4">
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white shadow-md ${accent.solid}`}>
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-bold text-[var(--teqa-text)]" style={{ fontFamily: "var(--font-display)" }}>{group.title}</span>
        <span className="block text-[11px] leading-snug text-[var(--teqa-muted)]">{group.description}</span>
      </span>
      {mobile ? (
        <HeaderButton label="Fermer le menu" onClick={onClose}>
          <X className="h-4 w-4" />
        </HeaderButton>
      ) : pinned ? (
        <HeaderButton label="Réduire le menu" onClick={onCollapse}>
          <ChevronsLeft className="h-4 w-4" />
        </HeaderButton>
      ) : (
        <span className="flex shrink-0 gap-0.5">
          <HeaderButton label="Épingler le menu" onClick={onPin}>
            <PanelLeftOpen className="h-4 w-4" />
          </HeaderButton>
          <HeaderButton label="Fermer" onClick={onClose}>
            <X className="h-4 w-4" />
          </HeaderButton>
        </span>
      )}
    </div>
  );
}

function HeaderButton({ label, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[var(--teqa-hint)] transition hover:bg-[var(--teqa-surface2)] hover:text-[var(--teqa-text)]"
    >
      {children}
    </button>
  );
}

function RailAction({ label, onClick, onHover, onLeave, danger = false, className = "flex", children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={(e) => onHover(e, label)}
      onMouseLeave={onLeave}
      aria-label={label}
      className={`h-10 w-10 items-center justify-center rounded-xl transition ${className} ${
        danger ? "text-[var(--teqa-muted)] hover:bg-[var(--teqa-red-dim)] hover:text-[var(--teqa-red)]" : "text-[var(--teqa-muted)] hover:bg-[var(--teqa-surface2)] hover:text-[var(--teqa-text)]"
      }`}
    >
      {children}
    </button>
  );
}

// Tuile d'une page : icône colorée + nom + description
function PageTile({ item, accent, active, groupLabel }) {
  const Icon = item.icon;
  const content = (
    <>
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-transform duration-200 ${
          item.disabled ? "bg-[var(--teqa-surface2)] text-[var(--teqa-hint)]" : active ? `${accent.solid} text-white` : `${accent.soft} group-hover:scale-110`
        }`}
      >
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={`block truncate text-[13px] font-semibold ${
            item.disabled ? "text-[var(--teqa-hint)]" : active ? accent.text : "text-[var(--teqa-text)]"
          }`}
        >
          {item.label}
        </span>
        <span className="block truncate text-[11px] text-[var(--teqa-hint)]">{groupLabel ? `${groupLabel} · ${item.hint || ""}` : item.hint}</span>
      </span>
      {item.disabled ? (
        <span className="shrink-0 rounded-full bg-[var(--teqa-surface2)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--teqa-warning)]">
          Bientôt
        </span>
      ) : (
        <ChevronRight
          className={`h-4 w-4 shrink-0 transition-all duration-200 ${
            active ? `${accent.text} opacity-100` : "-translate-x-1 text-[var(--teqa-hint)] opacity-0 group-hover:translate-x-0 group-hover:opacity-100"
          }`}
        />
      )}
    </>
  );

  const base = "group flex items-center gap-3 rounded-r-xl p-2 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--teqa-green)]";

  if (item.disabled) {
    return (
      <span className={`${base} cursor-not-allowed`} aria-disabled="true">
        {content}
      </span>
    );
  }
  return (
    <Link
      to={item.path}
      aria-current={active ? "page" : undefined}
      className={`${base} ${active ? accent.active : "border-l-[3px] border-transparent hover:bg-[var(--teqa-surface2)]"}`}
    >
      {content}
    </Link>
  );
}

export default Sidebar;
